/**
 * WebSocket Service for communicating with the backend
 * 
 * LOGGING CONTROLS:
 * - In production, most logging is disabled by default
 * - In development mode, detailed logging is enabled
 * - To enable verbose logging in production for debugging:
 *   - Open browser console and run: websocketService.setVerboseLogging(true)
 *   - To disable: websocketService.setVerboseLogging(false)
 *   - To clear error throttle cache: websocketService.clearErrorThrottle()
 * 
 * This helps reduce console noise while maintaining debugging capabilities when needed.
 */

import { WEBSOCKET_CONFIG, getWebSocketUrl } from '../config/websocket';
import { fetchAuthSession } from 'aws-amplify/auth';

// WebSocket service for communicating with the Selador Real Estate Backend
// 
// CONFIGURATION:
// To update the WebSocket URL, change the URL in src/config/websocket.ts
// The URL should be in the format: wss://{api-id}.execute-api.{region}.amazonaws.com/{stage}
// 
// Example: wss://abc123def.execute-api.us-west-2.amazonaws.com/production
// 
// NOTE: The WebSocket API must be deployed in AWS API Gateway before this will work.
// If you're getting connection errors, check that:
// 1. The WebSocket API is deployed in API Gateway
// 2. The API ID is correct
// 3. The stage name is correct
// 4. The Lambda function is integrated with the WebSocket API
//
// MESSAGE FILTERING:
// Technical message types (tool_use, reasoning, cycle_start, text_chunk) are filtered out
// to avoid showing confusing internal details to users. Only user-friendly messages
// (status, message with content, result, error) are emitted to the UI.

export interface WebSocketMessage {
  type: 'status' | 'text_chunk' | 'tool_use' | 'reasoning' | 'cycle_start' | 'message' | 'result' | 'error' | 'citations' | 'property_context_snapshot' | 'hello';
  timestamp: number;
  seq?: number; // Sequence number for message ordering and resume
  content?: string | Array<{text?: string, toolUse?: any}>;
  [key: string]: any;
}

export interface TextChunk {
  type: 'text_chunk';
  data: string;
  timestamp: number;
}

export interface ToolUse {
  type: 'tool_use';
  tool_name: string;
  tool_id: string;
  input: any;
  timestamp: number;
}

export interface Reasoning {
  type: 'reasoning';
  text: string;
  signature: string;
  timestamp: number;
}

export interface CycleStart {
  type: 'cycle_start';
  timestamp: number;
}

export interface MessageEvent {
  type: 'message';
  role: string;
  content?: string | Array<{text?: string, toolUse?: any}>;
  timestamp: number;
}

export interface ResultEvent {
  type: 'result';
  content: string | Array<{text?: string, toolUse?: any}>;
  agent: string;
  query_type: string;
  report_id?: string;
  timestamp: number;
}

export interface ErrorEvent {
  type: 'error';
  error: string;
  timestamp: number;
}

export interface CitationsEvent {
  type: 'citations';
  citations: Array<{
    id: number;
    source: string;
    source_link?: string;
    page: string;
    chunk: string;
    text: string;
  }>;
  timestamp: number;
}

export type WebSocketQueryType = 'overview' | 'question' | 'linked_question' | 'general' | string;

export interface WebSocketQuery {
  action: 'invoke';
  question: string;
  context?: string;
  query_type?: WebSocketQueryType;
  generate_report?: boolean;
  report_id?: string;
  address?: string;
  city?: string;
  state?: string;
  lat?: number;
  lng?: number;
  radius_meters?: number;
  /** Persisted analysis session (signed-in users) for follow-up continuity */
  session_id?: string;
}

export interface StreamingResponse {
  success: boolean;
  content?: string | Array<{text?: string, toolUse?: any}>;
  error?: string;
  agent?: string;
  query_type?: string;
  report_id?: string;
  session_id?: string;
  tools_available?: number;
  tools_used?: number;
}

export interface WebSocketStatus {
  message: string;
  timestamp: number;
}

export class WebSocketService {
  private ws: WebSocket | null = null;
  private messageHandlers: Map<string, Array<(message: WebSocketMessage) => void>> = new Map();
  private connectionPromise: Promise<void> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = WEBSOCKET_CONFIG.RECONNECT_ATTEMPTS;
  private reconnectDelay = WEBSOCKET_CONFIG.RECONNECT_DELAY;
  private isStreaming = false;
  private currentStreamId: string | null = null;
  private isProcessingMessage = false; // Prevent recursive message processing
  private readonly MAX_HANDLERS_PER_TYPE = 10; // Prevent excessive handlers
  private errorLogThrottle: Map<string, number> = new Map(); // Rate limit error logging
  private readonly ERROR_LOG_THROTTLE_MS = 5000; // 5 seconds between error logs
  private verboseLogging: boolean = false;

  // Sequence tracking and resume functionality
  private lastSequence: number = 0;
  private connectionId: string | null = null;
  private isReconnecting = false;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private readonly HEARTBEAT_INTERVAL = 30000; // 30 seconds
  private readonly HEARTBEAT_TIMEOUT = 10000; // 10 seconds
  private heartbeatTimeout: NodeJS.Timeout | null = null;
  private isVisible = true;
  private reconnectOnVisibility = false;
  private connectionIdWaiters: Array<(id: string) => void> = [];

  constructor(private url: string) {
    this.setupVisibilityHandlers();
    this.loadPersistedState();
  }

  // Build a connect URL that always includes a token query param.
  // For anonymous users we send "anon" so optional authorizers still execute.
  private async buildConnectionUrl(): Promise<string> {
    let token = 'anon';
    try {
      const session = await fetchAuthSession();
      token =
        session.tokens?.idToken?.toString() ||
        session.tokens?.accessToken?.toString() ||
        'anon';
    } catch {
      token = 'anon';
    }
    const separator = this.url.includes('?') ? '&' : '?';
    return `${this.url}${separator}token=${encodeURIComponent(token)}`;
  }

  // Helper method to throttle error logging
  private logErrorThrottled(key: string, message: string, error?: any): void {
    const now = Date.now();
    const lastLog = this.errorLogThrottle.get(key) || 0;
    
    if (now - lastLog > this.ERROR_LOG_THROTTLE_MS) {
      this.errorLogThrottle.set(key, now);
    }
  }

  // Method to enable/disable verbose logging for debugging
  setVerboseLogging(enabled: boolean): void {
    this.verboseLogging = enabled;
    // Store the setting in localStorage for persistence
    localStorage.setItem('websocket_verbose_logging', enabled.toString());
  }

  // Method to check if verbose logging is enabled
  isVerboseLoggingEnabled(): boolean {
    return localStorage.getItem('websocket_verbose_logging') === 'true';
  }

  // Method to clear error throttle cache (useful for debugging)
  clearErrorThrottle(): void {
    this.errorLogThrottle.clear();
    
  }

  // Setup mobile-friendly visibility handlers
  private setupVisibilityHandlers(): void {
    if (typeof document !== 'undefined') {
      // Handle visibility changes (mobile app switching)
      document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
      
      // Handle page show/hide (iOS Safari specific)
      window.addEventListener('pageshow', this.handlePageShow.bind(this));
      window.addEventListener('pagehide', this.handlePageHide.bind(this));
      
      // Handle online/offline status
      window.addEventListener('online', this.handleOnline.bind(this));
      window.addEventListener('offline', this.handleOffline.bind(this));
    }
  }

  // Load persisted state from sessionStorage
  private loadPersistedState(): void {
    if (typeof sessionStorage !== 'undefined') {
      const savedSeq = sessionStorage.getItem('ws_last_sequence');
      const savedConnId = sessionStorage.getItem('ws_connection_id');
      
      if (savedSeq) {
        this.lastSequence = parseInt(savedSeq, 10) || 0;
      }
      
      if (savedConnId) {
        this.connectionId = savedConnId;
      }
    }
  }

  // Save state to sessionStorage
  private saveState(): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('ws_last_sequence', this.lastSequence.toString());
      if (this.connectionId) {
        sessionStorage.setItem('ws_connection_id', this.connectionId);
      }
    }
  }

  // Handle visibility change events
  private handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.isVisible = true;
      if (this.reconnectOnVisibility) {
        this.reconnectOnVisibility = false;
        this.reconnectWithResume();
      }
    } else {
      this.isVisible = false;
      this.saveState();
      this.stopHeartbeat();
    }
  }

  // Handle page show event (iOS Safari)
  private handlePageShow(event: PageTransitionEvent): void {
    this.isVisible = true;
    if (event.persisted && this.ws?.readyState !== WebSocket.OPEN) {
      // Page was restored from cache, reconnect
      this.reconnectWithResume();
    }
  }

  // Handle page hide event (iOS Safari)
  private handlePageHide(): void {
    this.isVisible = false;
    this.saveState();
    this.stopHeartbeat();
  }

  // Handle online event
  private handleOnline(): void {
    if (this.isVisible && this.ws?.readyState !== WebSocket.OPEN) {
      this.reconnectWithResume();
    }
  }

  // Handle offline event
  private handleOffline(): void {
    this.stopHeartbeat();
  }

  // Connect to WebSocket
  async connect(): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      this.buildConnectionUrl()
        .then((wsUrl) => {
          this.ws = new WebSocket(wsUrl);

          this.ws.onopen = () => {
            
            this.reconnectAttempts = 0;
            this.reconnectDelay = 1000;
            // The API Gateway assigns a fresh connection id per socket, so
            // drop any id persisted from a previous page load until the
            // server echoes back a `hello` for THIS connection.
            this.connectionId = null;
            if (typeof sessionStorage !== 'undefined') {
              sessionStorage.removeItem('ws_connection_id');
            }
            this.startHeartbeat();
            resolve();
          };

          this.ws.onmessage = (event) => {
            try {
              const message: WebSocketMessage = JSON.parse(event.data);
              
              // Track sequence numbers
              if (message.seq && message.seq > this.lastSequence) {
                this.lastSequence = message.seq;
                this.saveState();
              }
              
              // Handle pong responses
              if ((message as any).type === 'pong') {
                if (this.heartbeatTimeout) {
                  clearTimeout(this.heartbeatTimeout);
                  this.heartbeatTimeout = null;
                }
                return; // Don't emit pong messages
              }
              
              // Store stream_id if present in message
              if ((message as any).stream_id) {
                this.setCurrentStreamId((message as any).stream_id);
              }
              
              // Route messages based on type
              if (message.type === 'status') {
                // Skip empty status messages to reduce noise
                if (message.content && Object.keys(message.content).length > 0) {
                  this.emit('status', message);
                }
              } else if (message.type === 'text_chunk') {
                this.emit('text_chunk', message);
              } else if (message.type === 'tool_use') {
                this.emit('tool_use', message);
              } else if (message.type === 'reasoning') {
                this.emit('reasoning', message);
              } else if (message.type === 'cycle_start') {
                this.emit('cycle_start', message);
              } else if (message.type === 'message') {
                this.emit('message', message);
              } else if (message.type === 'result') {
                // If backend attaches citations to the final result, emit those first
                if ((message as any).citations && Array.isArray((message as any).citations)) {
                  this.emit('citations', {
                    type: 'citations',
                    citations: (message as any).citations,
                    timestamp: (message as any).timestamp ?? Date.now()
                  });
                }
                this.emit('result', message);              
              } else if (message.type === 'citations') {
                this.emit('citations', message);
              } else if (message.type === 'property_context_snapshot') {
                this.emit('property_context_snapshot', message);
              } else if ((message as any).type === 'hello') {
                const incomingId = (message as any).connection_id;
                if (typeof incomingId === 'string' && incomingId) {
                  this.connectionId = incomingId;
                  this.saveState();
                  const waiters = this.connectionIdWaiters;
                  this.connectionIdWaiters = [];
                  waiters.forEach((resolve) => resolve(incomingId));
                }
                this.emit('hello', message);
              }
            } catch (error) {}
          };

          this.ws.onerror = (error) => {
            
            reject(new Error(`WebSocket connection failed to ${wsUrl}`));
          };

          this.ws.onclose = (event) => {
            this.ws = null;
            this.connectionPromise = null;
            this.isStreaming = false;
            this.currentStreamId = null;
            this.stopHeartbeat();
            
            // Attempt to reconnect if not a clean close
            if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
              if (this.isVisible) {
                this.scheduleReconnect();
              } else {
                this.reconnectOnVisibility = true;
              }
            }
          };

          // Set connection timeout
          setTimeout(() => {
            if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
              
              this.ws.close();
              reject(new Error('WebSocket connection timeout'));
            }
          }, WEBSOCKET_CONFIG.CONNECTION_TIMEOUT);
        })
        .catch((error) => {
          reject(error);
        });

    });

    return this.connectionPromise;
  }

  // Reconnect with resume functionality
  private async reconnectWithResume(): Promise<void> {
    if (this.isReconnecting) {
      return;
    }
    
    this.isReconnecting = true;
    
    try {
      await this.connect();
      
      // If we have a last sequence, try to resume
      if (this.lastSequence > 0 && this.connectionId) {
        await this.requestResume();
      }
      
      this.startHeartbeat();
    } catch (error) {
      console.error('Reconnect with resume failed:', error);
      this.scheduleReconnect();
    } finally {
      this.isReconnecting = false;
    }
  }

  // Request resume from server
  private async requestResume(): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    
    try {
      const resumeMessage = {
        action: 'resume',
        since_seq: this.lastSequence,
        connection_id: this.connectionId
      };
      
      this.ws.send(JSON.stringify(resumeMessage));
    } catch (error) {
      console.error('Failed to request resume:', error);
    }
  }

  // Start heartbeat
  private startHeartbeat(): void {
    this.stopHeartbeat();
    
    if (!this.isVisible) {
      return; // Don't start heartbeat when not visible
    }
    
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN && this.isVisible) {
        this.sendPing();
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  // Stop heartbeat
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  // Send ping message
  private sendPing(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    
    try {
      this.ws.send(JSON.stringify({ action: 'ping', timestamp: Date.now() }));
      
      // Set timeout for pong response
      this.heartbeatTimeout = setTimeout(() => {
        console.warn('Heartbeat timeout, reconnecting...');
        if (this.isVisible) {
          this.reconnectWithResume();
        } else {
          this.reconnectOnVisibility = true;
        }
      }, this.HEARTBEAT_TIMEOUT);
      
    } catch (error) {
      console.error('Failed to send ping:', error);
    }
  }

  // Schedule reconnection attempt
  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    
    setTimeout(() => {
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.connectWithResume().catch(error => {
          
          // Exponential backoff
          this.reconnectDelay = Math.min(this.reconnectDelay * 2, 10000);
        });
      }
    }, this.reconnectDelay);
  }

  // Send a query to the backend and return a promise that resolves with the complete response
  async sendQuery(query: Omit<WebSocketQuery, 'action'>): Promise<StreamingResponse> {
    await this.connect();
    
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }

    return new Promise((resolve, reject) => {
      const streamId = `stream_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      this.currentStreamId = streamId;
      this.isStreaming = true;
      
      // Store stream ID for resume capability
      this.setCurrentStreamId(streamId);

      // Set up message handlers for this specific query
      const statusHandler = (message: WebSocketMessage) => {
        if (message.type === 'status') {
          // Don't emit - this handler is already registered as a message handler
          // The component will receive the event directly
        }
      };

      const textChunkHandler = (message: WebSocketMessage) => {
        if (message.type === 'text_chunk') {
          // Don't emit - this handler is already registered as a message handler
          // The component will receive the event directly
        }
      };

      // Handle tool usage events (for debugging/transparency)
      const toolUseHandler = (message: WebSocketMessage) => {
        if (message.type === 'tool_use') {
          // Don't emit - this handler is already registered as a message handler
          // The component will receive the event directly
        }
      };

      // Handle reasoning events (for transparency)
      const reasoningHandler = (message: WebSocketMessage) => {
        if (message.type === 'reasoning' && message.text) {
          // Don't emit - this handler is already registered as a message handler
          // The component will receive the event directly
        }
      };

      // Handle cycle start events
      const cycleStartHandler = (message: WebSocketMessage) => {
        if (message.type === 'cycle_start') {
          // Don't emit - this handler is already registered as a message handler
          // The component will receive the event directly
        }
      };

      const messageHandler = (message: WebSocketMessage) => {
        if (message.type === 'message') {
          // Don't emit - this handler is already registered as a message handler
          // The component will receive the event directly
        }
      };

      // Register all handlers for this query
      this.onMessage('status', statusHandler);
      this.onMessage('text_chunk', textChunkHandler);
      this.onMessage('tool_use', toolUseHandler);
      this.onMessage('reasoning', reasoningHandler);
      this.onMessage('cycle_start', cycleStartHandler);
      this.onMessage('message', messageHandler);

      // Handle result events - only for promise resolution, don't emit to avoid conflicts
      const resultHandler = (message: WebSocketMessage) => {
        if (message.type === 'result') {
          // Don't emit result - let the component handle it
          
          // Resolve the promise with the final result
          let content = message.content;
          if (Array.isArray(content)) {
            // Extract text content from array, filtering out toolUse
            const textItems = content.filter(item => item.text && typeof item.text === 'string');
            content = textItems.map(item => item.text).join(' ');
          }
          
          const response: StreamingResponse = {
            success: true,
            content: content,
            agent: message.agent,
            query_type: message.query_type,
            report_id: (message as any).report_id,
            session_id: (message as any).session_id,
          };
          cleanup();
          resolve(response);
        }
      };

      // Handle error events - only for promise resolution, don't emit to avoid conflicts
      const errorHandler = (message: WebSocketMessage) => {
        if (message.type === 'error') {
          reject(new Error(message.error || 'Unknown error'));
          cleanup();
        }
      };

      // Handle citations events - only for promise resolution, don't emit to avoid conflicts
      const citationsHandler = (message: WebSocketMessage) => {
        if (message.type === 'citations' && message.citations) {
          // Don't emit citations - let the component handle it
        }
      };

      // Register the remaining handlers
      this.onMessage('result', resultHandler);
      this.onMessage('error', errorHandler);
      this.onMessage('citations', citationsHandler);

      // Cleanup function to remove all handlers
      const cleanup = () => {
        // Only remove the temporary handlers we created for this query
        // Don't remove the component's persistent handlers
        this.offMessage('status', statusHandler);
        this.offMessage('text_chunk', textChunkHandler);
        this.offMessage('tool_use', toolUseHandler);
        this.offMessage('reasoning', reasoningHandler);
        this.offMessage('cycle_start', cycleStartHandler);
        this.offMessage('message', messageHandler);
        this.offMessage('result', resultHandler);
        this.offMessage('error', errorHandler);
        this.offMessage('citations', citationsHandler);
        
        this.isStreaming = false;
        this.currentStreamId = null;
        // Clear stream ID when stream completes successfully
        this.clearCurrentStreamId();
      };

      // Send the query AFTER registering all handlers
      const queryMessage = {
        action: 'invoke',
        ...query,
        stream_id: streamId
      };
      
      if (this.ws) {
        this.ws.send(JSON.stringify(queryMessage));
      } else {
        reject(new Error('WebSocket connection lost'));
        return;
      }

      // Set a timeout to prevent hanging
      setTimeout(() => {
        if (this.isStreaming) {
          this.isStreaming = false;
          reject(new Error('Query timeout - no response received'));
        }
      }, WEBSOCKET_CONFIG.QUERY_TIMEOUT); // Use configured timeout (2 minutes)
    });
  }

  // Register a message handler
  onMessage(type: string, handler: (message: WebSocketMessage) => void): void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    
    const handlers = this.messageHandlers.get(type)!;
    if (handlers.length >= this.MAX_HANDLERS_PER_TYPE) return;
    
    handlers.push(handler);
  }

  // Remove a message handler
  offMessage(type: string, handler?: (message: WebSocketMessage) => void): void {
    if (handler) {
      const handlers = this.messageHandlers.get(type) || [];
      const newHandlers = handlers.filter(h => h !== handler);
      this.messageHandlers.set(type, newHandlers);
    } else {
      this.messageHandlers.delete(type);
    }
  }

  // Handle incoming messages
  private handleMessage(message: WebSocketMessage): void {
    // Prevent recursive message processing
    if (this.isProcessingMessage) {
      return;
    }

    // Check if WebSocket is properly connected
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    this.isProcessingMessage = true;
    
    try {
      const handlers = this.messageHandlers.get(message.type) || [];
      
      // Call handlers without triggering recursive message processing
      handlers.forEach((handler, index) => {
        try {
          handler(message);
        } catch (error) {
          this.logErrorThrottled(`handler_${message.type}_${index}`, `Error in handler ${index + 1} for type: ${message.type}:`, error);
        }
      });
    } finally {
      this.isProcessingMessage = false;
    }
  }

  // Emit an event (for status updates) - modified to prevent infinite loops
  private emit(type: string, data: any): void {
    // Prevent recursive emission to avoid infinite loops
    if (this.isProcessingMessage) {
      return;
    }
    
    if (this.messageHandlers.has(type)) {
      const handlers = this.messageHandlers.get(type)!;
      
      handlers.forEach((handler, index) => {
        try {
          handler(data);
        } catch (error) {
          this.logErrorThrottled(`emit_${type}_handler`, `Error in ${type} handler:`, error);
        }
      });
    }
  }

  // Disconnect WebSocket
  disconnect(): void {
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.connectionPromise = null;
    this.messageHandlers.clear();
    this.isStreaming = false;
    this.currentStreamId = null;
    this.isProcessingMessage = false; // Reset processing flag
  }

  // Check if connected
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  // Return the current connection id if we've been told about it by the
  // server (via a "hello" event), otherwise null.
  getConnectionId(): string | null {
    return this.connectionId;
  }

  // Ask the server to tell us our own connection id. Resolves with the id
  // as soon as the matching "hello" event arrives.
  async requestConnectionId(timeoutMs: number = 5000): Promise<string | null> {
    try {
      await this.connect();
    } catch {
      return null;
    }
    if (this.connectionId) {
      return this.connectionId;
    }
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return null;
    }
    try {
      // "get_connection_id" is the dedicated WebSocket route registered in
      // API Gateway. The handler responds by pushing a { type: 'hello',
      // connection_id } frame which we capture in the onmessage handler.
      this.ws.send(
        JSON.stringify({ action: 'get_connection_id', timestamp: Date.now() }),
      );
    } catch {
      return null;
    }
    return new Promise<string | null>((resolve) => {
      let settled = false;
      const finish = (value: string | null) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };
      this.connectionIdWaiters.push((id) => finish(id));
      window.setTimeout(() => finish(this.connectionId), timeoutMs);
    });
  }

  // Check if currently streaming
  isCurrentlyStreaming(): boolean {
    return this.isStreaming;
  }

  // Get connection state
  getConnectionState(): string {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'closed';
      default:
        return 'unknown';
    }
  }

  // Cancel current streaming operation
  cancelStream(): void {
    if (this.isStreaming && this.currentStreamId) {
      this.isStreaming = false;
      this.currentStreamId = null;
      // Note: We can't actually cancel the backend operation, but we can stop listening
    }
  }

  // Test WebSocket connection without establishing persistent connection
  async testConnection(): Promise<{ success: boolean; error?: string; details?: any }> {
    return new Promise((resolve) => {
      this.buildConnectionUrl().then((wsUrl) => {
        const testWs = new WebSocket(wsUrl);
        let resolved = false;
        
        const cleanup = () => {
          if (!resolved) {
            resolved = true;
            testWs.close();
          }
        };
        
        testWs.onopen = () => {
          cleanup();
          resolve({ success: true });
        };
        
        testWs.onerror = (error) => {
          cleanup();
          resolve({ success: false, error: 'WebSocket error during test connection' });
        };
        
        testWs.onclose = (event) => {
          if (!resolved) {
            cleanup();
            resolve({ 
              success: false, 
              error: `Connection closed with code ${event.code}`,
              details: {
                url: wsUrl,
                closeCode: event.code,
                closeReason: event.reason,
                readyState: testWs.readyState
              }
            });
          }
        };
        
        // Set test timeout
        setTimeout(() => {
          if (!resolved) {
            cleanup();
            resolve({ 
              success: false, 
              error: 'Test connection timeout',
              details: {
                url: wsUrl,
                readyState: testWs.readyState
              }
            });
          }
        }, WEBSOCKET_CONFIG.TEST_TIMEOUT); // 5 second timeout for testing
      }).catch((error) => {
        resolve({ success: false, error: 'WebSocket test creation failed' });
      });
    });
  }

  // Get detailed connection information
  getConnectionInfo(): { url: string; readyState: number; readyStateText: string } {
    const readyStateText = this.ws ? 
      ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][this.ws.readyState] || 'UNKNOWN' : 'DISCONNECTED';
    
    return {
      url: this.url,
      readyState: this.ws?.readyState ?? -1,
      readyStateText
    };
  }

  // HTTP API client for stream resume
  private getHttpApiUrl(): string {
    // Convert WebSocket URL to HTTP API URL
    // wss://0l4m73rdel.execute-api.us-west-2.amazonaws.com/production
    // becomes https://0l4m73rdel.execute-api.us-west-2.amazonaws.com/production
    return this.url.replace('wss://', 'https://');
  }

  // Resume a stream by fetching backlog from the server
  async resumeStream(streamId: string): Promise<{ chunks: WebSocketMessage[], finalResult: WebSocketMessage | null, isComplete: boolean }> {
    try {
      const apiUrl = this.getHttpApiUrl();
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'resume_stream',
          stream_id: streamId
        })
      });

      if (!response.ok) {
        throw new Error(`Resume request failed: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        chunks: data.chunks || [],
        finalResult: data.final_result || null,
        isComplete: data.is_complete || false
      };
      
    } catch (error) {
      console.error('Failed to resume stream:', error);
      throw error;
    }
  }

  // Get or generate a stream ID for the current session
  private getCurrentStreamId(): string | null {
    return sessionStorage.getItem('current_stream_id');
  }

  private setCurrentStreamId(streamId: string): void {
    sessionStorage.setItem('current_stream_id', streamId);
  }

  private clearCurrentStreamId(): void {
    sessionStorage.removeItem('current_stream_id');
  }

  // Enhanced connect method that attempts to resume streams
  async connectWithResume(): Promise<void> {
    await this.connect();
    
    // Check if we have a stream to resume
    const streamId = this.getCurrentStreamId();
    if (streamId && this.isConnected()) {
      try {
        console.log(`🔄 Attempting to resume stream: ${streamId}`);
        const resumeData = await this.resumeStream(streamId);
        
        if (resumeData.chunks.length > 0 || resumeData.finalResult) {
          console.log(`📦 Resumed stream with ${resumeData.chunks.length} chunks, complete: ${resumeData.isComplete}`);
          
          // Emit all the chunks to rebuild the UI state
          resumeData.chunks.forEach(chunk => {
            this.emit(chunk.type, chunk);
          });
          
          // Emit final result if available
          if (resumeData.finalResult) {
            this.emit(resumeData.finalResult.type, resumeData.finalResult);
          }
          
          // Clear stream ID if complete
          if (resumeData.isComplete) {
            this.clearCurrentStreamId();
          }
        } else {
          // No data to resume, clear the stream ID
          this.clearCurrentStreamId();
        }
      } catch (error) {
        console.error('Failed to resume stream:', error);
        // Clear invalid stream ID
        this.clearCurrentStreamId();
      }
    }
  }

  // Cleanup method for component unmount
  cleanup(): void {
    this.stopHeartbeat();
    
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
      window.removeEventListener('pageshow', this.handlePageShow.bind(this));
      window.removeEventListener('pagehide', this.handlePageHide.bind(this));
      window.removeEventListener('online', this.handleOnline.bind(this));
      window.removeEventListener('offline', this.handleOffline.bind(this));
    }
    
    if (this.ws) {
      this.ws.close(1000, 'Component cleanup');
    }
  }
}

// Create and export a singleton instance
export const websocketService = new WebSocketService(getWebSocketUrl()); 